export function nowIso() {
    return new Date().toISOString();
}
/** UUID v4 using the platform crypto (Node 19+, browsers, workers). */
export function newId() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === "function")
        return c.randomUUID();
    // Fallback (should not happen on supported runtimes).
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++)
        bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const h = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
export function normalizeEmail(value) {
    return value.trim().toLowerCase();
}
/** Loose E.164 normalisation: keep digits, add + and a default country code (1) for 10 digit numbers. */
export function normalizePhone(value, defaultCountryCode = "1") {
    const digits = value.replace(/[^\d+]/g, "");
    if (!digits)
        return null;
    if (digits.startsWith("+")) {
        const d = digits.slice(1).replace(/\D/g, "");
        return d.length >= 7 ? `+${d}` : null;
    }
    const d = digits.replace(/\D/g, "");
    if (d.length === 10)
        return `+${defaultCountryCode}${d}`;
    if (d.length === 11 && d.startsWith("1"))
        return `+${d}`;
    if (d.length >= 7)
        return `+${d}`;
    return null;
}
export function normalizeHandle(value) {
    return value.trim().replace(/^@/, "").toLowerCase();
}
/** Canonical profile URL -> { platform, handle } for the lookup table. */
export function parseSocialUrl(url) {
    let u;
    try {
        u = new URL(url.startsWith("http") ? url : `https://${url}`);
    }
    catch {
        return null;
    }
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    const parts = u.pathname.split("/").filter(Boolean);
    if (host.endsWith("linkedin.com") && parts[0] === "in" && parts[1]) {
        const handle = parts[1].toLowerCase();
        return { platform: "linkedin", handle, canonicalUrl: `https://www.linkedin.com/in/${handle}` };
    }
    if ((host === "twitter.com" || host === "x.com") && parts[0] && !["home", "search", "i", "explore"].includes(parts[0])) {
        const handle = parts[0].toLowerCase();
        return { platform: "twitter", handle, canonicalUrl: `https://x.com/${handle}` };
    }
    if (host.endsWith("instagram.com") && parts[0] && !["p", "explore", "reel"].includes(parts[0])) {
        const handle = parts[0].toLowerCase();
        return { platform: "instagram", handle, canonicalUrl: `https://www.instagram.com/${handle}` };
    }
    if (host.endsWith("facebook.com") && parts[0] && parts[0] !== "profile.php") {
        const handle = parts[0].toLowerCase();
        return { platform: "facebook", handle, canonicalUrl: `https://www.facebook.com/${handle}` };
    }
    if (host === "github.com" && parts[0]) {
        const handle = parts[0].toLowerCase();
        return { platform: "github", handle, canonicalUrl: `https://github.com/${handle}` };
    }
    return null;
}
export function initialsOf(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return "?";
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
export function displayNameOf(first, last, fallback = "Unknown") {
    const n = [first, last].filter((p) => p && p.trim()).join(" ").trim();
    return n || fallback;
}
const SOURCE_PRIORITY = {
    manual: 100,
    hub: 90,
    extension: 80,
    enrichment: 70,
};
/** How much a profile value from this source counts when sources disagree: the owner's own edits first, imports last. */
export function sourcePriority(source) {
    return SOURCE_PRIORITY[source] ?? 50;
}
export function daysBetween(a, b = new Date()) {
    const da = typeof a === "string" ? new Date(a) : a;
    const db = typeof b === "string" ? new Date(b) : b;
    return Math.floor((db.getTime() - da.getTime()) / 86_400_000);
}
export function addDays(date, days) {
    const d = new Date(date.getTime());
    d.setUTCDate(d.getUTCDate() + days);
    return d;
}
export function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}
export function csvEscape(value) {
    const s = value == null ? "" : String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
export function toCsv(headers, rows) {
    return [headers.map(csvEscape).join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\n");
}
/**
 * Turns a raw contact field label into something readable. Apple Contacts exports its built-in labels wrapped as
 * `_$!<Work>!$_`; other sources pass plain text through unchanged.
 */
export function humanizeLabel(label) {
    if (!label)
        return null;
    let s = label.trim();
    const wrapped = s.match(/^_\$!<(.*)>!\$_$/);
    if (wrapped)
        s = wrapped[1];
    s = s.replace(/FAX$/, " fax").trim();
    return s.length ? s : null;
}
//# sourceMappingURL=util.js.map